import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, ShoppingBag, Loader2, X, ListMusic } from "lucide-react";
import { toast } from "sonner";
import { usePlayer } from "@/context/PlayerContext";
import { fmtPrice, fmtTime, startCheckout, errorText } from "@/lib/api";
import { ease } from "./Reveal";

export function BuyButton({ track, item = null, compact = false }) {
  const [busy, setBusy] = useState(false);
  const price = item != null && Array.isArray(track.items) ? track.items[item].price : track.price;
  const buy = async () => {
    setBusy(true);
    try {
      await startCheckout(track.checkoutId ?? track.id, item);
    } catch (e) {
      toast.error(errorText(e));
      setBusy(false);
    }
  };
  return (
    <button
      data-testid={`release-buy-button-${track.id}${item != null ? `-${item}` : ""}${compact ? "-bar" : ""}`}
      onClick={buy}
      disabled={busy}
      className="flex items-center gap-2 rounded-full border border-[var(--gold)]/60 px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] text-[var(--gold)] transition-colors hover:bg-[var(--gold)] hover:text-[var(--ink)] disabled:opacity-60"
    >
      {busy ? <Loader2 size={13} className="animate-spin" /> : <ShoppingBag size={13} />}
      {compact ? fmtPrice(price) : `Buy · ${fmtPrice(price)}`}
    </button>
  );
}

function TracksModal({ track, onClose }) {
  const { track: current, playing, toggle } = usePlayer();
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm"
    >
      <motion.div
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 60, opacity: 0 }}
        transition={{ duration: 0.5, ease }}
        onClick={(e) => e.stopPropagation()}
        data-testid={`release-tracks-modal-${track.id}`}
        className="max-h-[80vh] w-full max-w-xl overflow-y-auto rounded-2xl border border-white/10 bg-[#131316] p-6 md:p-8"
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img src={track.cover} alt="" className="h-14 w-14 rounded-md object-cover" />
            <div>
              <p className="font-display text-2xl font-extrabold uppercase leading-none">{track.title}</p>
              <p className="mt-1 font-mono text-xs uppercase tracking-[0.15em] text-[var(--gold)]">
                {track.items.length} tracks · {fmtPrice(track.items[0].price)} each
              </p>
            </div>
          </div>
          <button data-testid="release-tracks-modal-close" onClick={onClose} className="text-[var(--stone)] hover:text-[var(--bone)]" aria-label="Close"><X size={20} /></button>
        </div>
        <ul className="mt-6 divide-y divide-white/10">
          {track.items.map((it, i) => {
            const pseudo = { id: `${track.id}-item-${i}`, checkoutId: track.id, title: it.name, artists: it.artist || "", cover: track.cover, preview: it.preview, previewSeconds: it.previewSeconds || 40, price: it.price, itemIndex: i, items: null };
            const active = current?.id === pseudo.id;
            return (
              <li key={it.name} className="flex items-center justify-between gap-4 py-3">
                <div className="flex min-w-0 items-center gap-4">
                  <button
                    data-testid={`release-track-play-${track.id}-${i}`}
                    onClick={() => toggle(pseudo)}
                    aria-label={active && playing ? "Pause" : "Listen"}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/20 transition-colors hover:border-[var(--gold)] hover:text-[var(--gold)]"
                  >
                    {active && playing ? <Pause size={13} fill="currentColor" /> : <Play size={13} fill="currentColor" className="ml-0.5" />}
                  </button>
                  <div className="min-w-0">
                    <p className="truncate text-base" data-testid={`release-track-name-${track.id}-${i}`}>{it.name}</p>
                    {it.artist && it.artist !== it.name && <p className="text-xs text-[var(--stone)]">{it.artist}</p>}
                  </div>
                </div>
                <BuyButton track={track} item={i} />
              </li>
            );
          })}
        </ul>
      </motion.div>
    </motion.div>
  );
}

export function ReleaseCard({ track, index }) {
  const { track: current, playing, toggle } = usePlayer();
  const [showTracks, setShowTracks] = useState(false);
  const active = current?.id === track.id;
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
  };
  return (
    <motion.article
      data-testid={`release-item-${track.id}`}
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 1, ease, delay: (index % 4) * 0.1 }}
      className="group"
    >
      <div onMouseMove={onMove} className="relative aspect-square overflow-hidden bg-[var(--ink-2)]">
        <img src={track.cover} alt={`Cover ${track.title}`} className={`h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105 ${active && playing ? "scale-105" : ""}`} />
        <div className="spotlight pointer-events-none absolute inset-0 opacity-0 mix-blend-screen transition-opacity duration-500 group-hover:opacity-100" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        {active && playing && (
          <div className="eq absolute right-4 top-4 flex h-5 items-end gap-[3px]" data-testid={`release-playing-${track.id}`}>
            <span /><span /><span /><span />
          </div>
        )}
        <button
          data-testid={`release-play-button-${track.id}`}
          onClick={() => toggle(track)}
          aria-label={active && playing ? "Pause" : "Listen"}
          className="absolute bottom-4 left-4 flex h-14 w-14 items-center justify-center rounded-full bg-[var(--bone)] text-[var(--ink)] shadow-xl transition-transform duration-300 hover:scale-110 active:scale-95"
        >
          {active && playing ? <Pause size={20} fill="currentColor" /> : <Play size={20} fill="currentColor" className="ml-0.5" />}
        </button>
      </div>
      <div className="mt-5">
        <div className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.15em] text-[var(--stone)]">
          <span>{track.key}</span>
        </div>
        <h3 className={`mt-2 font-display text-3xl font-extrabold leading-none uppercase ${track.smallTitle ? "tracking-wide" : ""}`}>{track.title}</h3>
        {track.artists && <p className="mt-1 text-sm text-[var(--stone)]">{track.artists}</p>}
        {track.label && <p className="text-sm font-bold text-[var(--stone)]">{track.label}</p>}
        {!track.items?.length ? <div className="mt-4"><BuyButton track={track} /></div> : null}
        {track.items?.length > 0 && (
          <button
            data-testid={`release-tracks-toggle-${track.id}`}
            onClick={() => setShowTracks(true)}
            className="group mt-4 flex items-center gap-3 rounded-full border border-[var(--gold)]/60 px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] text-[var(--gold)] transition-colors hover:bg-[var(--gold)] hover:text-[var(--ink)]"
          >
            <span className="flex items-center gap-2"><ListMusic size={13} /> {track.items.length} tracks</span>
            <span className="text-[var(--stone)] transition-colors group-hover:text-[var(--ink)]">· {fmtPrice(track.items[0].price)} each</span>
          </button>
        )}
      </div>
      <AnimatePresence>
        {showTracks && <TracksModal track={track} onClose={() => setShowTracks(false)} />}
      </AnimatePresence>
    </motion.article>
  );
}
